// ===========================================
// Cloudflare Turnstile script loader (#412)
// ===========================================
//
// ⚠️ The ONLY third-party script in the app (fonts are self-hosted so that no
// page whose URL carries a magic-link token makes a third-party request; see
// CLAUDE.md "Fonts are SELF-HOSTED"). It is injected on demand, ONCE, and only
// by `TurnstileWidget`, which only the public enquiry form and the public Meet &
// Greet booking page render (pinned by turnstileScope.test.ts). Never add it to
// index.html. Plan md/plan/412.md D5, D9.

export const TURNSTILE_SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

export interface TurnstileRenderOptions {
  sitekey: string;
  action?: string;
  appearance?: 'always' | 'execute' | 'interaction-only';
  size?: 'normal' | 'flexible' | 'compact';
  theme?: 'light' | 'dark' | 'auto';
  'refresh-expired'?: 'auto' | 'manual' | 'never';
  callback?: (token: string) => void;
  'expired-callback'?: () => void;
  'error-callback'?: () => void;
  'timeout-callback'?: () => void;
  'unsupported-callback'?: () => void;
}

export interface TurnstileApi {
  render: (container: HTMLElement, options: TurnstileRenderOptions) => string;
  reset: (widgetId?: string) => void;
  remove: (widgetId?: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let loading: Promise<TurnstileApi> | null = null;

/**
 * Load the Turnstile script once and resolve with `window.turnstile`.
 * A failed load clears the single-flight promise, so a later call can retry.
 */
export function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (loading) return loading;

  loading = new Promise<TurnstileApi>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = TURNSTILE_SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (window.turnstile) resolve(window.turnstile);
      else reject(new Error('Turnstile script loaded without window.turnstile'));
    };
    script.onerror = () => {
      // Leave no dead tag behind: a retry injects a fresh one (/qa Phase 5 nit 5).
      script.remove();
      reject(new Error('Turnstile script failed to load'));
    };
    document.head.appendChild(script);
  }).catch((error: unknown) => {
    loading = null;
    throw error;
  });

  return loading;
}

/** Test seam: forget the single-flight promise. */
export function resetTurnstileLoaderForTests(): void {
  loading = null;
}
